import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { Delete, SwapVert } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import { CMSContext } from "../context/CaseManagmentContext";
import { toast } from "react-toastify";
import { deductCredits } from "../../../utils/HelperFunction/deductCredits";
import { showInsufficientCreditsModal } from "../../../actions/creditActions";
import ConfirmationDialog from "../../../dialogs/ConfirmationDialog";
import AddNewCaseModal from "../../../dialogs/AddNewCaseDialog";
import CenteredPagination from "../../UI/CenteredPagination";
import { deleteCaseApi } from "../../../services/api/caseManagementApi";
const CaseHistory = () => {
  const {
    cases,
    fetchCases,
    advocates,
    clients,
    setSelectedCase,
    setCurrentTab,
    setIsHistoryClicked,
  } = useContext(CMSContext);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [deleteModal, setDeleteModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const [selectedItem, setSelectedItem] = useState({});

  const [sortConfig, setSortConfig] = useState({
    key: "title",
    direction: "asc",
  });
  const [openEditModal, setOpenEditModal] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(false);

  // Handle sorting logic
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  // Apply category filter
  const categoryFilteredData =
    selectedCategory && selectedCategory !== "all"
      ? cases?.filter((item) => item.status === selectedCategory)
      : cases;

  const sortedData =
    categoryFilteredData &&
    [
      ...categoryFilteredData?.filter((title) => title?.caseTitle !== null),
    ]?.sort((a, b) => {
      const isAsc = sortConfig.direction === "asc";

      if (sortConfig.key === "timecreated") {
        // Handle date sorting using Moment.js
        const dateA = moment(a.createdAt, "DD-MM-YYYY");
        const dateB = moment(b.createdAt, "DD-MM-YYYY");

        // Handle invalid dates gracefully
        if (!dateA.isValid() || !dateB.isValid()) return 0;

        return isAsc ? dateA - dateB : dateB - dateA;
      }

      // Default sorting for other keys
      if (sortConfig.key === "name") {
        return isAsc
          ? a.caseTitle.localeCompare(b.caseTitle)
          : b.caseTitle.localeCompare(a.caseTitle);
      }

      return 0; // If no valid sort key, return 0 (no sorting)
    });

  // Handle search functionality
  const filteredData = sortedData?.filter((item) =>
    item?.caseTitle.toLowerCase().includes(searchTerm?.toLowerCase()),
  );

  // Handle pagination
  const paginatedData = filteredData?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const handleCategoryChange = (value) => {
    setPage(1);
    setSelectedCategory(value);
  };
  const handleDeleteCase = async (caseId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    setLoading(true);
    try {
      const response = await deleteCaseApi(caseId);

      if (response.status === 200) {
        fetchCases();
        toast.success("Case deleted successfully");
        const deductionSuccess = deductCredits(
          {
            userId,
            creditsToDeduct: 1,
            event: "Delete Case on Case Management",
          },
          dispatch,
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification.",
          );
        }
      } else {
        const data = response.data;
        console.error("Error deleting client:", data);
      }
    } catch (error) {
      console.error("Error deleting client:", error);
    } finally {
      setLoading(false);
      setDeleteModal(false);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          boxSizing: "border-box",
          flexGrow: 1,
          bgcolor: "#fff",
          borderRadius: "8px",
          p: { xs: "8px", md: "16px 16px 8px 16px" },
        }}
      >
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: { xs: 1 },
            mb: { xs: 1, lg: 0 },
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
              maxWidth: "400px",
              mt: { xs: 0, lg: 1 },
              mb: { xs: 0, lg: 2 },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ccc",
                },
                "&:hover fieldset": {
                  borderColor: "#00509e",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#00509e",
                },
              },
              "& input": {
                padding: "10px",
                fontSize: "0.875rem",
              },
            }}
            placeholder="Search by title"
            value={searchTerm}
            onChange={(e) => {
              setPage(1);
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "#303f9f", }} />
              ),
              sx: {
                pl: 1,
                backgroundColor: "#ffffff",
                borderRadius: "20px",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                display: { xs: "none", md: "block" },
                marginRight: "4px",
                fontSize: "13px",
                color: "#555",
                fontWeight: "500",
              }}
              htmlFor="resultsCount"
            >
              Filter by Status:
            </InputLabel>
            <FormControl
              variant="outlined"
              sx={{
                minWidth: 80,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  height: { xs: "36px", lg: "30px" },
                  padding: "8px",
                },
                "& fieldset": {
                  borderColor: "#ddd",
                  borderWidth: "1px",
                },
                "& .MuiSelect-icon": {
                  right: "4px",
                },
                "& .MuiSelect-select": {
                  padding: "0px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <Select
                id="filterByCategory"
                value={selectedCategory}
                onChange={(e) => {
                  handleCategoryChange(e.target.value);
                }}
                sx={{
                  fontSize: "13px",
                  height: "30px",
                  bgcolor: "#fff",
                }}
              >
                <MenuItem value={"all"} sx={{ fontSize: "0.875rem" }}>
                  All
                </MenuItem>

                {[
                  ...new Set(
                    cases
                      ?.filter((title) => title.caseTitle !== null)
                      ?.map((title) => title.status),
                  ),
                ].map((category, index) => (
                  <MenuItem
                    value={category}
                    key={`${category}-${index}`}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "80%",
            boxShadow: "none",
            borderRadius: "12px",
            overflowY: "auto",
            "::-webkit-scrollbar": {
              width: "5px",
            },
            "::-webkit-scrollbar-track": {
              background: "#fff",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#bbb",
              borderRadius: "6px",
            },
            "::-webkit-scrollbar:horizontal": {
              height: "4px",
            },
            "::-webkit-scrollbar-thumb:horizontal": {
              background: "#bbb",
              borderRadius: "6px",
            },
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Title
                    <IconButton
                      onClick={() => handleSort("caseTitle")}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <SwapVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>

                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  Advocates
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  Clients
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Created At
                    <IconButton
                      onClick={() => handleSort("timecreated")}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <SwapVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#101936", py: 2 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData?.length > 0 ? (
                paginatedData?.map((item, index) => (
                  <TableRow
                    key={`${item.chat_id}-${index}`}
                    sx={{
                      fontSize: "13px",
                      backgroundColor: "white",
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
                      },
                      "&:not(:last-child)": {
                        borderBottom: "1px solid #f0f0f0",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCase(item);
                      setCurrentTab(1);
                      setIsHistoryClicked(false);
                      setAnchorEl(null);
                    }}
                  >
                    <TableCell
                      sx={{
                        py: 2,
                        color: "#101936",
                        fontSize: "13px",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "#3C50B1",
                        },
                      }}
                      onClick={() => {
                        setSelectedItem(item);
                      }}
                    >
                      {truncateText(item.caseTitle, 100)}
                    </TableCell>

                    <TableCell
                      sx={{
                        py: 2,
                        fontSize: "13px",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.caseDescription}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        fontSize: "13px",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.lawyerDetails
                        ?.map((advocate) => advocate?.name)
                        .filter((name) => name)
                        .join(", ")}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        fontSize: "13px",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.clients
                        ?.map(
                          (clientId) =>
                            clients.find((x) => x._id === clientId)?.name,
                        )
                        .filter((name) => name) // Filter out undefined names
                        .join(", ")}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        fontSize: "13px",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {moment(item.createdAt, "DD-MM-YYYY").isValid()
                        ? moment(item.createdAt, "DD-MM-YYYY").format("LL")
                        : item.createdAt}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        fontSize: "13px",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.status}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          sx={{
                            color: "inherit",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedItem(item);
                            setAnchorEl(event.currentTarget);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          PaperProps={{
                            sx: {
                              //mt: 3.5,
                              boxShadow:
                                "0px 4px 12px rgba(0, 0, 0, 0.1) !important",
                              borderRadius: "8px",
                            },
                          }}
                        >
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCase(selectedItem);
                              setCurrentTab(1);
                              setIsHistoryClicked(false);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon>
                              <VisibilityIcon
                                sx={{
                                  color: "#4CAF50",
                                  width: "15px",
                                  height: "auto",
                                }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Preview
                            </Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenEditModal(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon>
                              <FontAwesomeIcon
                                size="xs"
                                icon={faPenToSquare}
                                style={{ color: "#2196f3" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Edit
                            </Typography>
                          </MenuItem>

                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon size="xs">
                              <Delete
                                fontSize="small"
                                sx={{
                                  color: "#d2042d",
                                  fontSize: "16px",
                                }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Delete
                            </Typography>
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography sx={{ fontSize: "14px" }}>
                      No records found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            fontSize: { xs: "12px", md: "14px" },
            mt: 1,
            width: "100%",
            overflowY: "auto",
            "& .MuiTablePagination-root": {
              width: "100%",

              maxWidth: { xs: "100%", sm: "80%", md: "60%" },
            },
            "& .MuiTablePagination-toolbar": {
              p: 0,
              flexWrap: "wrap",
              justifyContent: "center",
            },
            "& .MuiSelect-select": {
              fontSize: { xs: "12px", md: "14px" },
            },
            "& .MuiInputBase-root": {
              fontSize: { xs: "12px", md: "14px" },
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            },
          }}
        >
          <CenteredPagination
            totalResults={filteredData?.length}
            resultsCount={rowsPerPage}
            currentPage={page}
            onPageChange={handleChangePage}
          />
        </Box>
      </Box>
      {openEditModal && (
        <AddNewCaseModal
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          data={selectedItem}
        />
      )}
      {deleteModal && (
        <ConfirmationDialog
          open={deleteModal}
          onClose={() => {
            setDeleteModal(false);
          }}
          onConfirm={() => {
            if (deleteModal) {
              handleDeleteCase(selectedItem?._id);
            }
          }}
          title="Confirm Delete"
          contentText={`Are you sure you want to delete the Case "${selectedItem?.caseTitle}"? This action cannot be undone.`}
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          loading={loading}
        />
      )}
    </Box>
  );
};

export default CaseHistory;
